/* KQ C13 - PRODUCT PAGE BEHAVIOUR

   Four behaviours. The two click behaviours are delegated from document in the
   CAPTURE phase so they run before the theme's own handlers and survive Shopify
   re-rendering a section. Nothing patches a component's internals.

   1. [data-kq-buy] and .sticky-add-to-cart__button
      Scroll to the size pickers. The sticky bar preselects the first available
      variant, so on a two-selector bundle its button would quietly drop an
      XS/XS in the cart for someone who never chose. It is relabelled in
      kq-pdp.css and re-pointed here.

   2. [data-kq-checkout]
      Straight to checkout with whatever sizes are showing. The add-to-cart
      block is deliberately disabled on this product, so this is the only
      purchase path.

      It posts to /cart/add.js and then sends them to /checkout, the same
      endpoint the theme's own add-to-cart uses, so if the theme could sell it,
      this can. The customer never sees a cart. Verified end to end against a
      live product: the bundle expands to all four components and the sizes map
      correctly across a mismatched pair.

      An earlier version used a cart permalink (/cart/<variant>:<qty>). That is
      rejected with "Link no longer exists" for a product not published to the
      Online Store, and it is legacy. Kept only as a fallback.

   3. Per garment stock readouts
      One "N left" under the jersey row and another under the shirt row, instead
      of a single count for the combination. Numbers come from the JSON the
      KQ Per Garment Stock block prints; see that block for what they mean.

      Injected rather than authored inline because each readout has to sit
      INSIDE its size fieldset, which the theme owns.

      Three things this got wrong first, all found by testing:

      The observer watches document.body, NOT .product-details. Choosing a
      variant makes the theme replace the whole .product-details node, so an
      observer bound to it is left watching a detached element and never fires
      again.

      Re-render is queued with setTimeout, NOT requestAnimationFrame. rAF does
      not fire in a hidden tab, so a customer who picked a size, switched tabs
      and came back would have found the readouts gone.

      Each readout spans all grid columns. See buildReadout.

   4. Collection day
      The KQ Collection Day block offers Thursday or Saturday, with NOTHING
      preselected. The choice is REQUIRED: pressing Buy with no day blocks the
      checkout, scrolls to the block and flashes it red (.kq-collect--error,
      styled in the block). Choosing a day clears the error.

      The choice is carried to the order as a LINE ITEM PROPERTY on the cart
      add. Shopify expands the bundle into its four components and keeps the
      property on the BUNDLE GROUP, not on each component, so in admin it shows
      under the bundle heading on the order.

      Shopify itself only knows ONE pickup location for this product, so the
      checkout names the festival either way. This property is the only record
      of which day the customer actually chose.

      The radios live inside .product-details, which the theme replaces wholesale
      on a variant change, so the choice is remembered here and re-applied after
      each re-render. Without that, picking a size would silently reset the day. */

(function () {
  var SCROLL_TRIGGER = '.sticky-add-to-cart__button, [data-kq-buy]';
  var CHECKOUT_TRIGGER = '[data-kq-checkout]';
  var COLLECT_NAME = 'kq-collect';
  var COLLECT_PROPERTY = 'Collection';
  var COLLECT_ERROR = 'kq-collect--error';

  /* Option position, not option name, so renaming "Jersey Size" is safe. */
  var OPTION_INDEXES = ['0', '1'];

  function cartAddUrl() {
    if (window.Theme && Theme.routes && Theme.routes.cart_add_url) {
      return Theme.routes.cart_add_url;
    }
    return '/cart/add.js';
  }

  function goBuy() {
    var target =
      document.getElementById('kqBuy') ||
      document.querySelector('[class*="variant-picker"]') ||
      document.querySelector('.product-details');

    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function productForm() {
    return document.querySelector('form[action*="/cart/add"]');
  }

  function currentVariantId() {
    var form = productForm();
    var input = form
      ? form.querySelector('[name="id"]')
      : document.querySelector('[name="id"]');
    return input && input.value ? input.value : null;
  }

  function currentQuantity() {
    var form = productForm();
    var input = form ? form.querySelector('[name="quantity"]') : null;
    var n = input ? parseInt(input.value, 10) : 1;
    return n > 0 ? n : 1;
  }

  /* ---- collection day --------------------------------------------------- */

  var chosenCollect = null;

  function collectSelector(value) {
    var s = 'input[name="' + COLLECT_NAME + '"]';
    return value ? s + '[value="' + value + '"]' : s;
  }

  function collectBlock() {
    return document.querySelector('.kq-collect');
  }

  function currentCollectLabel() {
    var el = document.querySelector(collectSelector() + ':checked');
    if (!el) return null;
    return el.getAttribute('data-kq-collect-label') || el.value;
  }

  function restoreCollect() {
    if (!chosenCollect) return;
    var el = document.querySelector(collectSelector(chosenCollect));
    if (el && !el.checked) el.checked = true;
  }

  function flagCollect(noScroll) {
    var block = collectBlock();
    if (!block) return;

    /* Remove, force a reflow, re-add: restarts the flash on every press. */
    block.classList.remove(COLLECT_ERROR);
    void block.offsetWidth;
    block.classList.add(COLLECT_ERROR);

    if (!noScroll && typeof block.scrollIntoView === 'function') {
      block.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function clearCollectError() {
    var block = collectBlock();
    if (block) block.classList.remove(COLLECT_ERROR);
  }

  /* ---- social handle ----------------------------------------------------

     Optional. Sent as the "Social" line item property, beside Collection,
     so it lands on the bundle in admin and on the confirmation email.
     The field sits in .product-details, which the theme replaces on every
     size change, so what they typed is remembered here and put back. */

  var SOCIAL_FIELD = '[data-kq-social]';
  var SOCIAL_PROPERTY = 'Social';
  var typedSocial = '';

  function currentSocial() {
    var el = document.querySelector(SOCIAL_FIELD);
    var v = (el ? el.value : typedSocial) || '';
    v = v.replace(/\s+/g, '');
    if (!v || v === '@') return null;
    /* A pasted profile link is kept as is, a bare name gets its @. */
    if (v.indexOf('/') === -1 && v.charAt(0) !== '@') v = '@' + v;
    return v.slice(0, 80);
  }

  function restoreSocial() {
    if (!typedSocial) return;
    var el = document.querySelector(SOCIAL_FIELD);
    if (el && !el.value) el.value = typedSocial;
  }

  /* ---- sizes: nothing preselected -----------------------------------------

     The theme always has a variant checked, so both size rows would look
     chosen on arrival. Until the customer taps a size in a row, that row is
     shown as unpicked (.kq-size--unpicked: no highlight, no stock readout) and
     Buy is blocked. Pressing Buy with a row unpicked flashes it red
     (.kq-size--error, styled in snippets/kq-bundle-look.liquid). The picks are
     remembered here because the theme replaces the rows on every change. */

  var SIZE_UNPICKED = 'kq-size--unpicked';
  var SIZE_ERROR = 'kq-size--error';
  var pickedSize = {};
  var sizeErrorOn = false;

  function sizeGroup(idx) {
    var input = document.querySelector('input[data-fieldset-index="' + idx + '"]');
    if (!input || !input.closest) return null;
    return input.closest('fieldset') || input.closest('.variant-option');
  }

  function unpickedGroups() {
    var out = [];
    for (var i = 0; i < OPTION_INDEXES.length; i++) {
      var g = sizeGroup(OPTION_INDEXES[i]);
      if (g && !pickedSize[OPTION_INDEXES[i]]) out.push(g);
    }
    return out;
  }

  function pickSize(input) {
    var idx = input && input.getAttribute('data-fieldset-index');
    if (idx === null || idx === undefined) return;
    pickedSize[idx] = true;
    if (!unpickedGroups().length) sizeErrorOn = false;
    scheduleSync();
  }

  function renderSizes() {
    for (var i = 0; i < OPTION_INDEXES.length; i++) {
      var idx = OPTION_INDEXES[i];
      var g = sizeGroup(idx);
      if (!g) continue;
      var unpicked = !pickedSize[idx];
      if (g.classList.contains(SIZE_UNPICKED) !== unpicked) g.classList.toggle(SIZE_UNPICKED, unpicked);
      var err = unpicked && sizeErrorOn;
      if (g.classList.contains(SIZE_ERROR) !== err) g.classList.toggle(SIZE_ERROR, err);
    }
  }

  function flagSizes(groups) {
    sizeErrorOn = true;
    for (var i = 0; i < groups.length; i++) {
      /* Remove, force a reflow, re-add: restarts the flash on every press. */
      groups[i].classList.remove(SIZE_ERROR);
      void groups[i].offsetWidth;
      groups[i].classList.add(SIZE_ERROR);
    }
    if (groups[0] && typeof groups[0].scrollIntoView === 'function') {
      groups[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  /* ---- checkout --------------------------------------------------------- */

  function goCheckout(button) {
    var id = currentVariantId();
    var qty = currentQuantity();

    if (!id) {
      /* No variant resolved, so send them to the pickers rather than
         guessing a size on their behalf. */
      goBuy();
      return;
    }

    var collect = currentCollectLabel();

    /* Both sizes must be picked by the customer. If the day is missing too,
       that block flashes as well, but the scroll goes to the sizes. */
    var missing = unpickedGroups();
    if (missing.length) {
      flagSizes(missing);
      if (!collect && collectBlock()) flagCollect(true);
      return;
    }

    /* A day is required. Only skip the check if the block is missing from
       the page entirely, so a disabled block never locks the store. */
    if (!collect && collectBlock()) {
      flagCollect();
      return;
    }

    var item = { id: id, quantity: qty };

    var social = currentSocial();

    if (collect || social) {
      item.properties = {};
      if (collect) item.properties[COLLECT_PROPERTY] = collect;
      if (social) item.properties[SOCIAL_PROPERTY] = social;
    }

    if (button) {
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Taking you to checkout';
    }

    fetch(cartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart add failed');
        window.location.href = '/checkout';
      })
      .catch(function () {
        window.location.href = '/cart/' + id + ':' + qty;
      });
  }

  /* ---- per garment stock ------------------------------------------------ */

  function stockData() {
    var el = document.querySelector('[data-kq-stock-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function stockTone(n) {
    if (n <= 0) return ' kq-stock--out';
    if (n <= 5) return ' kq-stock--low';
    if (n <= 15) return ' kq-stock--mid';
    return '';
  }

  function stockLabel(n) {
    if (n <= 0) return 'Sold out';
    if (n === 1) return 'Last one';
    return n + ' left';
  }

  function buildReadout() {
    var node = document.createElement('p');
    node.setAttribute('data-kq-stock', '');

    /* The theme lays each size row out as a CSS grid of auto-fit columns, so an
       appended child lands in the next free CELL: beside XL on the five-button
       jersey row, and wrapped under XS on the six-button shirt row. Spanning
       every column puts it on its own line beneath the buttons in both. The
       flex properties cover the theme switching that row to flex later.

       Set here rather than in the block's stylesheet because this is a
       consequence of WHERE this script injects, not of how the readout looks. */
    node.style.gridColumn = '1 / -1';
    node.style.flexBasis = '100%';
    node.style.width = '100%';

    var dot = document.createElement('span');
    dot.className = 'kq-stock__dot';
    node.appendChild(dot);

    var text = document.createElement('span');
    text.setAttribute('data-kq-stock-text', '');
    node.appendChild(text);

    return node;
  }

  function renderStock() {
    var data = stockData();
    if (!data) return;

    for (var i = 0; i < OPTION_INDEXES.length; i++) {
      var idx = OPTION_INDEXES[i];
      var table = data[idx];
      if (!table) continue;

      var selector = 'input[data-fieldset-index="' + idx + '"]';
      var anyInput = document.querySelector(selector);
      if (!anyInput || !anyInput.closest) continue;

      var group = anyInput.closest('fieldset') || anyInput.closest('.variant-option');
      if (!group) continue;

      var checked = document.querySelector(selector + ':checked');
      if (!checked) continue;

      var n = table[checked.value];
      if (typeof n !== 'number') continue;

      var node = group.querySelector('[data-kq-stock]');
      if (!node) {
        node = buildReadout();
        /* In the row's heading, to the right of the Chart button. */
        var legend = group.querySelector('legend');
        if (legend) {
          node.style.gridColumn = '';
          node.style.flexBasis = '';
          node.style.width = '';
          node.setAttribute('data-kq-stock-inline', '');
        }
        (legend || group).appendChild(node);
      }

      /* Only touch the DOM on a real change, so the observer settles. */
      var cls = 'kq-stock' + stockTone(n);
      if (node.className !== cls) node.className = cls;

      var text = node.querySelector('[data-kq-stock-text]');
      var label = stockLabel(n);
      if (text && text.textContent !== label) text.textContent = label;
    }
  }

  /* ---- bundles left, under the buy button --------------------------------

     Every bundle takes one jersey, and jerseys (50) are the scarce garment, so
     bundles left = total jerseys left = the sum of the jersey row ("0") in the
     stock JSON. Summing the variants instead would overcount, because each
     jersey unit is counted once per shirt size.

     Styled by .kq-buy-now-note, which the KQ Buy Bundle block already defines.
     Injected, not authored, so the template never has to be rewritten for it. */

  var BUNDLE_TOTAL = 50;

  function bundlesLeft(data) {
    var row = data && data['0'];
    if (!row) return null;
    var total = 0;
    for (var k in row) {
      if (Object.prototype.hasOwnProperty.call(row, k) && typeof row[k] === 'number') {
        total += row[k];
      }
    }
    return total;
  }

  /* The closer's headline ("50 Bundles, then it's all gone.") counts down too.
     Only a headline that STARTS with a number is touched, so rewording it in
     the theme editor to something without a number switches this off. */
  var CLOSE_HEADLINE = '[id$="__kq_close"] .kq-h2';

  function renderHeadline(n) {
    var h = document.querySelector(CLOSE_HEADLINE);
    if (!h) return;
    if (!h.hasAttribute('data-kq-left-headline')) {
      if (!/^\s*\d+\s/.test(h.textContent)) return;
    }
    /* The count big, the warning smaller underneath. Inline
       styles because this markup only exists once the script has run. */
    var key = String(n);
    if (h.getAttribute('data-kq-left-headline') === key) return;
    h.setAttribute('data-kq-left-headline', key);

    /* Three lines: the count on top, "Bundles left" under it, the warning
       smaller at the bottom. Sold out drops the middle line. */
    var top = n <= 0 ? 'Sold out' : n + '/' + BUNDLE_TOTAL;
    var mid = n <= 0 ? '' : 'Bundles left';
    var bottom = n <= 0 ? 'It’s all gone.' : 'then it’s all gone.';

    /* kq-c13.css resets every span to the body font with no caps, so each
       line is told to inherit the headline's font, caps and tracking. */
    function line(text) {
      var el = document.createElement('span');
      el.style.display = 'block';
      el.style.fontFamily = 'inherit';
      el.style.textTransform = 'inherit';
      el.style.letterSpacing = 'inherit';
      el.textContent = text;
      return el;
    }

    h.textContent = '';
    var a = line(top);
    var b = line(bottom);
    b.style.fontSize = '.5em';
    b.style.marginTop = '.35em';
    h.appendChild(a);
    if (mid) h.appendChild(line(mid));
    h.appendChild(b);
  }

  function renderLeft() {
    var n = bundlesLeft(stockData());
    if (n === null) return;

    renderHeadline(n);

    /* Offer card, left of the price row: "48/50". Written into the
       card's own name slot, which the snippet only shows once this has run,
       so the old "NATGAT XX Bundle" name never flashes up. */
    var slot = document.querySelector('.kq-offer-head .kq-offer-name');
    if (slot) {
      var slotText = n <= 0 ? 'Sold out' : n + '/' + BUNDLE_TOTAL;
      if (slot.textContent !== slotText) slot.textContent = slotText;
      if (!slot.hasAttribute('data-kq-left-name')) slot.setAttribute('data-kq-left-name', '');
    }

    var button = document.querySelector(CHECKOUT_TRIGGER);
    if (!button) return;

    var node = document.querySelector('[data-kq-left]');
    if (!node) {
      node = document.createElement('p');
      node.className = 'kq-buy-now-note';
      node.setAttribute('data-kq-left', '');
    }
    if (node.previousElementSibling !== button) {
      button.insertAdjacentElement('afterend', node);
    }

    var label =
      n <= 0 ? 'Sold out' : n + ' of ' + BUNDLE_TOTAL + ' bundles left';
    if (node.textContent !== label) node.textContent = label;
  }

  /* ---- [Chart] next to each size label ----------------------------------

     Jumps the product gallery to the size chart, which is already one of the
     product's images (the one whose file name contains "Size_Chart"). It
     clicks that image's own thumbnail, so the theme's slideshow does the
     switching. Only added if that image is in the gallery.

     Injected into each size <legend>, which the theme re-renders on every
     variant change, so sync() puts it back each time. */

  var CHART_MATCH = 'Size_Chart';
  var CHART_TRIGGER = '[data-kq-chart]';

  function chartThumb() {
    var thumbs = document.querySelectorAll('media-gallery slideshow-controls button');
    for (var i = 0; i < thumbs.length; i++) {
      var img = thumbs[i].querySelector('img');
      if (img && (img.currentSrc || img.src || '').indexOf(CHART_MATCH) !== -1) {
        return thumbs[i];
      }
    }
    return null;
  }

  function showChart() {
    var thumb = chartThumb();
    if (thumb) thumb.click();

    /* On a phone the gallery sits above the pickers and is off screen. */
    var gallery = document.querySelector('media-gallery');
    if (gallery && typeof gallery.getBoundingClientRect === 'function') {
      var r = gallery.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight) {
        gallery.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  function renderChartLinks() {
    if (!chartThumb()) return;
    for (var i = 0; i < OPTION_INDEXES.length; i++) {
      var input = document.querySelector('input[data-fieldset-index="' + OPTION_INDEXES[i] + '"]');
      var group = input && input.closest ? input.closest('fieldset') : null;
      var legend = group ? group.querySelector('legend') : null;
      if (!legend || legend.querySelector(CHART_TRIGGER)) continue;

      var b = document.createElement('button');
      b.type = 'button'; /* inside the variant form, so never a submit */
      b.setAttribute('data-kq-chart', '');
      b.setAttribute('aria-label', 'Show size chart');
      b.textContent = 'Chart';
      /* A small grey pill. Inline because this node only exists once the
         script has run. */
      b.style.cssText =
        'display:inline-block;vertical-align:middle;margin:-3px 0 0 8px;' +
        'padding:3px 9px;border:1px solid rgba(255,255,255,.2);' +
        'border-radius:999px;background:rgba(255,255,255,.04);cursor:pointer;' +
        'font:inherit;line-height:1.2;letter-spacing:inherit;' +
        'text-transform:inherit;color:var(--kq-muted);text-decoration:none';
      legend.appendChild(b);
    }
  }

  /* ---- product title: two lines max ------------------------------------

     "NATGAT XX Shirt Bundle (Free Hat & Pin)" runs to three lines at the
     theme's size. This steps the font down 1px at a time until the title
     fits in two lines at the current width, so each screen only shrinks as
     much as it needs. The theme sets the size with !important, hence the
     priority flag. Re-fits after a re-render or a width change. */

  var TITLE = '.product-details h1';
  var TITLE_LINES = 2;
  var TITLE_MIN_PX = 18;

  function fitTitle() {
    var h = document.querySelector(TITLE);
    if (!h || !h.clientWidth) return;

    var key = h.clientWidth + '|' + h.textContent;
    if (h.getAttribute('data-kq-fit') === key) return;
    h.setAttribute('data-kq-fit', key);

    h.style.removeProperty('font-size');
    var cs = window.getComputedStyle(h);
    var size = parseFloat(cs.fontSize);
    var ratio = parseFloat(cs.lineHeight) / size || 1.1;

    while (size > TITLE_MIN_PX && h.offsetHeight > size * ratio * TITLE_LINES + 2) {
      size -= 1;
      h.style.setProperty('font-size', size + 'px', 'important');
    }
  }

  /* ---- sync ------------------------------------------------------------- */

  function sync() {
    fitTitle();
    renderChartLinks();
    renderStock();
    renderSizes();
    renderLeft();
    restoreCollect();
    restoreSocial();
  }

  var queued = false;
  function scheduleSync() {
    if (queued) return;
    queued = true;
    /* setTimeout, not rAF - see the note at the top of this file. */
    window.setTimeout(function () {
      queued = false;
      sync();
    }, 0);
  }

  function watchDom() {
    /* body, not .product-details - see the note at the top of this file. */
    var root = document.body || document.documentElement;
    if (!root || typeof MutationObserver === 'undefined') return;
    new MutationObserver(scheduleSync).observe(root, {
      childList: true,
      subtree: true
    });
  }

  /* ---- wiring ----------------------------------------------------------- */

  document.addEventListener(
    'click',
    function (event) {
      var node = event.target;
      if (!node || typeof node.closest !== 'function') return;

      if (node.closest(CHART_TRIGGER)) {
        event.preventDefault();
        event.stopPropagation();
        showChart();
        return;
      }

      var sizeLabel = node.closest('.variant-option__button-label');
      if (sizeLabel) {
        pickSize(sizeLabel.querySelector('input[data-fieldset-index]'));
      } else if (node.matches && node.matches('input[data-fieldset-index]')) {
        pickSize(node);
      }

      var checkout = node.closest(CHECKOUT_TRIGGER);
      if (checkout) {
        event.preventDefault();
        event.stopPropagation();
        goCheckout(checkout);
        return;
      }

      if (node.closest(SCROLL_TRIGGER)) {
        event.preventDefault();
        event.stopPropagation();
        goBuy();
      }
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      var t = event.target;
      if (t && t.matches && t.matches('input[data-fieldset-index]') && t.checked) pickSize(t);
      if (t && t.name === COLLECT_NAME && t.checked) {
        chosenCollect = t.value;
        clearCollectError();
      }
      scheduleSync();
    },
    true
  );

  document.addEventListener(
    'input',
    function (event) {
      var t = event.target;
      if (t && t.matches && t.matches(SOCIAL_FIELD)) typedSocial = t.value;
    },
    true
  );

  window.addEventListener('resize', scheduleSync);

  function start() {
    /* Seed from a checked radio if one exists, e.g. the browser restoring
       form state on Back. The block itself preselects nothing. */
    var initial = document.querySelector(collectSelector() + ':checked');
    if (initial) chosenCollect = initial.value;
    sync();
    watchDom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
